import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HasAccess } from "@/components/common/has-access";
import { ProfessionalStatus } from "../schema/professional-schema";
import { useInviteProfessionalToDashboard } from "../hooks/use-invite-professional-to-dashboard";

type Props = {
  professionalId: string;
  email?: string | null;
  status: string;
  /** Set once they accept the invitation; the webhook writes it, never the CRM. */
  clerkId?: string | null;
};

/**
 * Access to the professional dashboard, shown on the edit drawer.
 *
 * Like status, this is deliberately not part of the edit form: it is an action
 * with a side effect outside this record, not a field to save.
 */
export default function ProfessionalDashboardInvitePanel({
  professionalId,
  email,
  status,
  clerkId,
}: Props) {
  const { mutate, isPending } = useInviteProfessionalToDashboard(professionalId);

  const hasAccess = Boolean(clerkId);
  const isDismissed = status === ProfessionalStatus.FULLY_DISMISSED;
  const hasEmail = Boolean(email?.trim());

  // Mirrors the server's eligibility rules so the reason is visible before
  // anyone clicks, rather than arriving as a toast afterwards.
  const blockedReason = !hasEmail
    ? "Add an email address before inviting them."
    : isDismissed
    ? "Fully dismissed professionals cannot be given access."
    : null;

  return (
    <div className="py-5 px-6 border-b border-gray-200">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h4>Professional Dashboard</h4>
          <Badge variant={hasAccess ? "active" : "secondary"}>
            {hasAccess ? "Has access" : "No access"}
          </Badge>
        </div>

        {!hasAccess && (
          <HasAccess permission="write">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isPending || Boolean(blockedReason)}
              onClick={() => mutate()}
            >
              {isPending ? "Sending…" : "Invite to Professional Dashboard"}
            </Button>
          </HasAccess>
        )}
      </div>

      <p className="mt-2 text-xs text-gray-500">
        {hasAccess
          ? "They can sign in to the professional dashboard and see their own clients."
          : blockedReason ??
            `We'll email ${email?.trim()} an invitation. Their access is set up when they accept it.`}
      </p>
    </div>
  );
}
